'use client';

import { Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useAssistant } from './assistant-provider';

/**
 * Stable id the overlay uses to return focus here when it closes/minimizes.
 * Relies on `AssistantRoot` being mounted once (see `app/(app)/layout.tsx`) —
 * a second mount would duplicate this DOM id.
 */
export const ASSISTANT_BUBBLE_ID = 'assistant-bubble-trigger';

/**
 * The launcher, bottom-right above everything except dialogs.
 *
 * Hidden outright when the workspace cannot use the assistant — no API key, or
 * a paused budget. A spent monthly allowance is NOT one of those cases: the
 * backend keeps `enabled: true` so the overlay can explain itself. See
 * `lib/assistant/session.ts#assistantIsVisible`, which this component consumes
 * via `visible` rather than re-deriving.
 */
export function AssistantBubble() {
  const { visible, overlay, open, streaming, hasPendingActions } = useAssistant();

  if (!visible || overlay === 'open') return null;

  return (
    <Button
      id={ASSISTANT_BUBBLE_ID}
      type="button"
      onClick={open}
      variant="brand"
      aria-label="Open the assistant"
      className="fixed bottom-4 right-4 z-40 size-12 rounded-full shadow-lg sm:bottom-6 sm:right-6"
    >
      <Sparkles className="size-5" />
      {streaming && (
        <span
          aria-hidden="true"
          className="absolute right-1 top-1 size-2.5 animate-pulse rounded-full bg-emerald-400"
        />
      )}
      {hasPendingActions && !streaming && (
        <span
          aria-hidden="true"
          className={cn(
            'absolute -right-0.5 -top-0.5 size-3 rounded-full',
            'border-2 border-background bg-amber-500',
          )}
        />
      )}
    </Button>
  );
}
